// CONTRACT:C3-CAPABILITY-PLUGIN.1.0
/** THE DOGFOOD CAPABILITY — a real multi-INT capability that consumes the platform the
 *  prior phases hardened, built against ONLY the public C3 SDK (@tak-tdf/contracts) +
 *  its own bundled dataset. THIS PURITY IS THE 0.2a EXIT GATE.
 *
 *  THIS capability touches NOTHING but HostContext + the contract types — proving the SDK
 *  is self-sufficient. The dogfood-check.ts gate statically scans this file's import
 *  specifiers and FAILS if any resolve into apps/cop/src/host, packages/abac-pdp, or
 *  another host internal. (As of Increment 3, tracksCapability.ts is ALSO pure — the
 *  engine-write bypass that let it cheat is closed; the no-bypass-check.ts gate enforces
 *  that no capability touches the engine's write surface.)
 *
 *  CLEARANCE-FILTERING WITHOUT TOUCHING THE PDP (the key seam): this capability only
 *  PUBLISHES its LOCAL multi-INT dataset via ctx.data.publish. The HOST records every
 *  published envelope, runs it through the PDP, and PAINTS only the cleared-visible slice
 *  for the current subject (the sole-writer closure — the capability never subscribes,
 *  never paints, never calls a PDP, never touches the engine). A denied envelope is never
 *  painted — Glomar; a low-clearance subject sees a STRICT SUBSET of a high-clearance one
 *  from the SAME dataset, decided entirely host-side.
 *
 *  ACTOR-AWARE / IRREVERSIBLE: it declares a CapabilityManifest (contribution kinds +
 *  required permissions + per-command actor allow-list) and contributes typed commands —
 *  including a `risk:"irreversible"` export (human-gated, OUTSIDE any model) and a read
 *  command — so the introspect emitter (apps/cop/src/introspect) advertises them onto the
 *  MCP/OpenAPI surface. */

import type {
  Capability,
  CapabilityManifest,
  CommandSpec,
  Disposable,
  HostContext,
  LayerSpec,
} from "../../../../packages/contracts-ts/src/index.ts";
import { MULTI_INT_DATASET, MULTI_INT_KIND } from "./multiIntDataset.ts";

/** The capability id. */
export const MULTI_INT_CAP_ID = "scop:cap:multi-int";

/** The deck layer this capability owns — a DISTINCT kind from tracksCapability so the
 *  two never fight over the same entities. */
const LAYER_ID = "scop:layer:multi-int";

/** The two command ids the capability contributes (a read + an irreversible export). */
export const MULTI_INT_CMD_FOCUS = "scop:cmd:multi-int-focus";
export const MULTI_INT_CMD_EXPORT = "scop:cmd:multi-int-export";

/** THE MANIFEST — declared BEFORE activation (AOA §3a). It names the contribution KINDS
 *  the capability may contribute (layer + command), the permissions it requires (gated
 *  via policy.decide before activation), and the per-command actor allow-list. The
 *  irreversible export is constrained to `human` ONLY — so an ai-agent/npe cannot
 *  self-dispatch it (paired with the host's irreversible human gate). */
export const MULTI_INT_MANIFEST: CapabilityManifest = {
  contributes: ["layer", "command"],
  permissions: [
    {
      subject: {},
      action: "subscribe",
      resource: {
        classification: "UNCLASSIFIED",
        releasability: [],
        mission: [],
        caveats: [],
        originator_control: false,
        policy_ref: "policy://defense.local",
        payload_mode: "clear",
        leakage_profile: "clear",
      },
    },
  ],
  commandActors: {
    // The irreversible export is HUMANS-ONLY at the manifest layer (the registration-time
    // half of the actor gate; the host's dispatch-time gate closes the other half).
    [MULTI_INT_CMD_EXPORT]: ["human"],
    // The read command may be driven by any actor class (read is safe to automate).
    [MULTI_INT_CMD_FOCUS]: ["human", "npe", "ai-agent"],
  },
};

/** The layer spec the capability contributes — a scatter of multi-INT observations,
 *  keyed on the `sensor` kind so it stays out of the tracks layer's lane. */
const MULTI_INT_LAYER: LayerSpec = {
  id: LAYER_ID,
  kinds: [MULTI_INT_KIND],
  type: "ScatterplotLayer",
  props: { radiusMeters: 6_000 },
  updateTriggers: { getFillColor: "int_discipline" },
};

/** Build the dogfood multi-INT capability. NO host argument, NO PDP — the host's seam
 *  does the clearance filtering for us. */
export function createMultiIntCapability(): Capability {
  let disposables: Disposable[] = [];

  return {
    id: MULTI_INT_CAP_ID,

    async activate(ctx: HostContext): Promise<void> {
      // 1. CONTRIBUTE the capability's OWN in-map layer (distinct kind ⇒ no clash with
      //    tracksCapability). registerLayer is manifest-gated AND host-mediated: the host
      //    creates the GPU layer and PAINTS the cleared-visible sensor envelopes itself
      //    (cop.ts's per-publish painter + projection subject re-gate). This capability
      //    never touches the engine's write surface — it only publishes (step 4 below) and
      //    the host renders what is cleared-visible (Glomar: a denied INT object is never
      //    painted, no marker/count). The sole-writer closure (Increment 3 / Move 2).
      const layerDisposable = ctx.registerLayer(MULTI_INT_LAYER);

      // 2. Contribute typed commands so the introspect emitter advertises them onto the
      //    MCP/OpenAPI surface and the host can apply the irreversible human gate.
      const focusCmd: CommandSpec = {
        id: MULTI_INT_CMD_FOCUS,
        title: "Focus the multi-INT picture",
        category: "Multi-INT",
        run: () => {
          // A READ verb: recenter the camera on the theater. Reversible; any actor class.
          ctx.engine.setView({ longitude: 56.6, latitude: 26.6, zoom: 8 });
        },
        permission: MULTI_INT_MANIFEST.permissions?.[0],
        actors: ["human", "npe", "ai-agent"],
        risk: "read",
      };

      const exportCmd: CommandSpec = {
        id: MULTI_INT_CMD_EXPORT,
        title: "Export the multi-INT layer",
        category: "Multi-INT",
        run: () => {
          // An IRREVERSIBLE verb (export an INT layer): exercises the host's mandatory
          // human gate. The host enforces the gate OUTSIDE any model — this run() is
          // reached ONLY after a human confirmation; an ai-agent/npe can never reach it.
          // (The actual export sink is host-side; here it is a no-op stub.)
        },
        input: {
          type: "object",
          properties: { layerId: { type: "string" } },
          required: ["layerId"],
          additionalProperties: false,
        },
        actors: ["human"],
        risk: "irreversible",
      };

      const focusDisposable = ctx.registerCommand(focusCmd);
      const exportDisposable = ctx.registerCommand(exportCmd);

      // layerDisposable (host-mediated) owns the GPU layer teardown; no engine cleanup here.
      disposables = [layerDisposable, focusDisposable, exportDisposable];

      // 3. PUBLISH the local dataset. Each envelope flows out through ctx.data.publish onto
      //    the bus; the HOST records it, runs it through the PDP, and paints ONLY the
      //    cleared-visible slice for the current subject (we never subscribe or paint).
      for (const env of MULTI_INT_DATASET) {
        await ctx.data.publish(env);
      }
    },

    deactivate(): void {
      for (const d of disposables.splice(0)) {
        try {
          d.dispose();
        } catch {
          // teardown is best-effort
        }
      }
    },
  };
}
